import {
  addMonths,
  format,
  getYear,
  isSameDay,
  isSameMonth,
  setYear,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { CalendarIcon, ChevronDownIcon } from 'lucide-react';
import {
  type ComponentProps,
  type CSSProperties,
  type FC,
  type HTMLAttributes,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { type DateRange } from 'react-day-picker';
import { type PortaledSurfaceVariant } from '../lib/portaledSurface';
import { cn } from '../lib/utils';
import { Button } from './Button';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { ScrollArea } from './ScrollArea';
import { buttonVariants } from './ui/button';
import { Calendar } from './ui/calendar';

// `Calendar` (react-day-picker's `DayPicker`) does not take a plain `date-fns` `Locale` —
// its `locale` prop is `Partial<DayPickerLocale>`, a `date-fns` locale *extended* with
// translated month/nav/day-name labels for the calendar's own UI (see `enUS`/`fr`/`es`
// re-exported from `react-day-picker/locale` below). Deriving the type from `Calendar`'s
// own declared prop gets this right without hand-declaring `Partial<DayPickerLocale>`.
export type DatePickerLocale = ComponentProps<typeof Calendar>['locale'];

// Re-exported so consumers never need their own `react-day-picker` dependency just to pass
// a translated locale to `DatePicker` — kept to the locales this app actually uses rather
// than the full ~120-locale catalogue `react-day-picker/locale` ships.
export type { DateRange } from 'react-day-picker';
export { enUS, es, fr } from 'react-day-picker/locale';

export interface DatePickerSingleProps {
  mode: 'single';
  selected: Date | undefined;
  onSelect: (date: Date | undefined) => void;
}

export interface DatePickerRangeProps {
  mode: 'range';
  selected: DateRange | undefined;
  onSelect: (range: DateRange | undefined) => void;
}

export type DatePickerProps = (DatePickerSingleProps | DatePickerRangeProps) & {
  /** Days after this one are disabled — e.g. footage that doesn't exist yet. */
  maxDate?: Date;
  /**
   * `react-day-picker`'s own type only accepts the 0–6 literal union; a plain `number`
   * is taken here so callers can pass an app-wide "first day of week" setting straight
   * through without narrowing it themselves.
   */
  weekStartsOn?: number;
  /** A DayPicker-compatible locale (e.g. this module's own re-exported `fr`) for month/day names and formatting. */
  locale?: DatePickerLocale;
  placeholder?: ReactNode;
  disabled?: boolean;
  className?: string;
  /** Surface theme for the portaled calendar. Use `"dark"` inside `DarkThemeScope`. */
  variant?: PortaledSurfaceVariant;
  /** Applied to the portaled calendar popover (e.g. a high z-index above the video page). */
  contentClassName?: string;
  'data-testid'?: string;
};

function formatDay(date: Date, locale: DatePickerLocale): string {
  // `Calendar`'s `locale` prop type (`Partial<DayPickerLocale>`) is a structural superset
  // of what `date-fns`'s own `format` expects — every locale this component is ever given
  // (`enUS`, `fr`, `es`, ...) is a complete object, never actually partial.
  return format(date, 'PP', { locale } as Parameters<typeof format>[2]);
}

// Takes the full discriminated union (not a `Pick`) so `mode` still narrows `selected` —
// `Pick<DatePickerProps, ...>` would collapse the union and decorrelate the two fields.
function getTriggerLabel(props: DatePickerProps): ReactNode {
  const { locale, placeholder = 'Pick a date' } = props;

  if (props.mode === 'single') {
    return props.selected ? formatDay(props.selected, locale) : placeholder;
  }

  const { selected } = props;
  if (!selected?.from) {
    return placeholder;
  }

  if (!selected.to || isSameDay(selected.from, selected.to)) {
    return formatDay(selected.from, locale);
  }

  return `${formatDay(selected.from, locale)} – ${formatDay(selected.to, locale)}`;
}

function getInitialMonth(props: DatePickerProps): Date {
  const anchor = props.mode === 'single' ? props.selected : props.selected?.from;
  return anchor ?? new Date();
}

// Puts the calendar on the month that holds `anchor`. A month already on screen stays as
// it is, so an update inside that month does not undo the month the user browsed to.
// Range mode shows `current` and the month after it.
function getAnchoredMonth(current: Date, anchor: Date | undefined, isRange: boolean): Date {
  if (!anchor) {
    return current;
  }
  if (isSameMonth(anchor, current) || (isRange && isSameMonth(anchor, addMonths(current, 1)))) {
    return current;
  }
  return startOfMonth(anchor);
}

// Effect deps use calendar-day values, not `Date` / `DateRange` identity. A parent that
// passes a new object for the same day must not re-run the open-draft sync.
function getDayKey(date: Date | undefined): number | undefined {
  return date ? startOfDay(date).getTime() : undefined;
}

function getRangeKey(range: DateRange | undefined): string | undefined {
  if (!range) {
    return undefined;
  }
  return `${getDayKey(range.from)}:${getDayKey(range.to)}`;
}

// Figma's `date` component (node `7075:23311`) spells out a two-letter weekday header
// ("Su Mo Tu We Th Fr Sa") regardless of locale — `date-fns`'s six-`E` token is the one
// format string that produces that short form instead of the single-letter narrow one.
function formatWeekdayName(day: Date, locale: DatePickerLocale): string {
  return format(day, 'EEEEEE', locale ? ({ locale } as Parameters<typeof format>[2]) : undefined);
}

// Matches `react-day-picker`'s own default caption format, so the year picker header reads
// exactly like the calendar header it replaces.
function formatCaption(month: Date, locale: DatePickerLocale): string {
  return format(month, 'LLLL y', locale ? ({ locale } as Parameters<typeof format>[2]) : undefined);
}

// The calendar is seven 36px cells wide. The year grid matches that column exactly so the
// popover never changes width when the two views swap.
const CALENDAR_COLUMN_WIDTH = 'w-[15.75rem]';

// Range mode shows two of those columns, each inside the calendar's own 12px padding.
// Pinned so the popover keeps its width when the year grid replaces both calendars.
const RANGE_POPOVER_WIDTH = 'w-[34.5rem]';

// The year grid stands in for the calendar's day table, so it takes that table's exact
// height and the popover does not resize when the two views swap. The table is the 12px
// weekday header (`label-sm` has a 12px line height) plus six 36px week rows separated by
// `mt-1`: 12 + (6 x 36) + (5 x 4) = 248px. `fixedWeeks` on every `Calendar` is what makes
// the row count six for every month rather than four to six.
const YEAR_GRID_HEIGHT = 'h-[15.5rem]';

const YEAR_PICKER_FIRST = 1900;
const YEAR_PICKER_LAST = 2099;
const YEAR_PICKER_YEARS = Array.from(
  { length: YEAR_PICKER_LAST - YEAR_PICKER_FIRST + 1 },
  (_, index) => YEAR_PICKER_FIRST + index,
);

interface CalendarCaptionToggleProps {
  expanded: boolean;
  onClick: () => void;
  children?: ReactNode;
}

/**
 * The month caption doubles as the year-picker toggle, so it is a real `Button` rather
 * than the plain `<span>` `react-day-picker` renders. It keeps the caption's existing
 * appearance and adds the hover and focus treatment every other ghost control has.
 */
const CalendarCaptionToggle: FC<CalendarCaptionToggleProps> = ({ expanded, onClick, children }) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onClick}
    aria-expanded={expanded}
    className={cn(
      'h-8 rounded-lg px-2 text-text-primary label-md',
      'hover:bg-interaction-secondary-background-default',
    )}
  >
    {children}
    <ChevronDownIcon
      className={cn('size-3.5 text-text-muted transition-transform', expanded && 'rotate-180')}
      aria-hidden
    />
  </Button>
);

interface YearPickerProps {
  /** The month the calendar is showing. Choosing a year changes only its year. */
  displayMonth: Date;
  locale: DatePickerLocale;
  maxDate?: Date;
  onSelectYear: (year: number) => void;
  onDismiss: () => void;
}

const YearPicker: FC<YearPickerProps> = ({
  displayMonth,
  locale,
  maxDate,
  onSelectYear,
  onDismiss,
}) => {
  const selectedYear = getYear(displayMonth);
  const selectedRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    const selected = selectedRef.current;
    const viewport = selected
      ?.closest('[data-slot="scroll-area"]')
      ?.querySelector<HTMLElement>('[data-slot="scroll-area-viewport"]');
    if (!selected || !viewport) return;

    // Scroll only the year-list viewport. `scrollIntoView` can also scroll the document
    // behind the portaled popover and move the popover away from its trigger.
    viewport.scrollTop = selected.offsetTop - (viewport.clientHeight - selected.offsetHeight) / 2;

    // Move keyboard focus into the year grid. Opening the picker unmounts the caption
    // toggle that had focus, so without this a keyboard user drops out of the popover and
    // cannot arrow through the years. `preventScroll` keeps the manual centering above.
    selected.focus({ preventScroll: true });
  }, []);

  return (
    <div className={cn('mx-auto flex flex-col gap-3', CALENDAR_COLUMN_WIDTH)}>
      {/* `h-9` matches the calendar's own 36px caption row, so the toggle does not move
          when the view changes. */}
      <div className="flex h-9 items-center justify-center">
        <CalendarCaptionToggle expanded onClick={onDismiss}>
          {formatCaption(displayMonth, locale)}
        </CalendarCaptionToggle>
      </div>
      <ScrollArea className={YEAR_GRID_HEIGHT}>
        <div className="grid grid-cols-4 gap-1 pr-2.5">
          {YEAR_PICKER_YEARS.map((year) => {
            const isSelected = year === selectedYear;
            return (
              <Button
                key={year}
                ref={isSelected ? selectedRef : undefined}
                type="button"
                variant="ghost"
                size="sm"
                disabled={maxDate ? year > getYear(maxDate) : false}
                aria-pressed={isSelected}
                onClick={() => onSelectYear(year)}
                className={cn(
                  'h-9 w-full rounded-lg px-0 text-text-primary label-sm',
                  'hover:bg-interaction-secondary-background-default',
                  'disabled:text-interaction-secondary-content-inactive',
                  isSelected && 'bg-background-accent',
                )}
              >
                {year}
              </Button>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};

// Apply the Figma tokens and dimensions through the generated shadcn Calendar API.
// This keeps CalendarDayButton, keyboard focus, and date semantics in the shadcn base.
const calendarClassNames = {
  months: 'relative flex',
  month: 'flex flex-col gap-3',
  // The nav row is absolutely positioned at the top of the month. Without a height it
  // collapses to its 32px buttons and centres them 2px above the 36px caption row, so it
  // is given the caption's height and centres its buttons on the same line.
  nav: 'absolute inset-x-0 top-0 flex h-[var(--cell-size)] w-full items-center justify-between gap-1',
  weekdays: 'flex',
  weekday: 'w-9 flex-none label-sm text-text-muted',
  week: 'flex mt-1 first:mt-0',
  // `Calendar` spreads this object over its own defaults, so every key here replaces the
  // shadcn string instead of merging with it. `group/day` must therefore be repeated by
  // hand: `CalendarDayButton` draws its keyboard focus ring through
  // `group-data-[focused=true]/day:`, and that variant matches nothing once the group name
  // is dropped.
  day: 'group/day w-9 select-none p-0',
  // `CalendarDayButton` merges this class string last through `cn` (tailwind-merge), so
  // each token below replaces the shadcn default of the same group. No `!` is required.
  day_button: cn(
    'text-text-primary label-sm',
    'hover:bg-interaction-secondary-background-default',
    'disabled:text-interaction-secondary-content-inactive',
    'data-[selected-single=true]:rounded-sm data-[selected-single=true]:bg-background-accent',
    'data-[selected-single=true]:text-text-primary',
    'data-[range-start=true]:rounded-l-sm data-[range-end=true]:rounded-r-sm',
    'data-[range-start=true]:bg-background-accent',
    'data-[range-middle=true]:bg-background-accent',
    'data-[range-end=true]:bg-background-accent',
    'data-[range-start=true]:text-text-primary',
    'data-[range-middle=true]:text-text-primary',
    'data-[range-end=true]:text-text-primary',
  ),
  range_start: 'rounded-l-sm bg-background-accent',
  range_middle: 'rounded-none bg-background-accent',
  range_end: 'rounded-r-sm bg-background-accent',
  // These target the day button from its cell. A descendant selector outranks the
  // button's own plain `text-*` class, and the `data-[selected*]` variants above
  // outrank the descendant selector, so a selected day keeps its own colour.
  outside: '[&_button]:text-text-muted',
  today: '[&_button]:text-text-accent',
  button_previous: cn(
    buttonVariants({ variant: 'ghost' }),
    'size-8 select-none rounded-lg border border-border-muted p-1',
  ),
  button_next: cn(
    buttonVariants({ variant: 'ghost' }),
    'size-8 select-none rounded-lg border border-border-muted p-1',
  ),
};

const calendarStyle = { '--cell-size': '2.25rem' } as CSSProperties;

/**
 * The shadcn Date Picker recipe: Popover + Button + generated Calendar.
 * This wrapper adds the Solink API and Figma-specific layout and confirmation behavior.
 *
 * Per the Figma spec (node `88:1608`), a click on a day only updates a *draft* selection
 * held inside this component — `onSelect` fires (and the popover closes) only on Apply;
 * Cancel, an outside click or Escape all discard the draft. Callers therefore still see
 * an immediate-commit `onSelect`, same as before; the draft never leaks out.
 */
export const DatePicker: FC<DatePickerProps> = (props) => {
  const { maxDate, weekStartsOn, locale, disabled, className, contentClassName, variant } = props;
  const [open, setOpen] = useState(false);
  const [draftSingle, setDraftSingle] = useState<Date | undefined>(
    props.mode === 'single' ? props.selected : undefined,
  );
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(
    props.mode === 'range' ? props.selected : undefined,
  );
  // The draft is a snapshot taken when the popover opens. A parent that updates `selected`
  // from a live source while the popover is open would therefore be reverted by Apply. An
  // untouched draft follows that newer value instead. Once the user picks a day the draft
  // wins, because re-seeding then would discard the selection the user is making.
  const [draftDirty, setDraftDirty] = useState(false);
  const liveSingle = props.mode === 'single' ? props.selected : undefined;
  const liveRange = props.mode === 'range' ? props.selected : undefined;
  const liveSingleKey = getDayKey(liveSingle);
  const liveRangeKey = getRangeKey(liveRange);
  const liveSingleRef = useRef(liveSingle);
  const liveRangeRef = useRef(liveRange);
  liveSingleRef.current = liveSingle;
  liveRangeRef.current = liveRange;
  // Range mode renders two independent, side-by-side single-month `Calendar`s (per the
  // Figma layout — each keeps its own prev/next chevrons); this is the month shown on
  // the left, the right one is always one month ahead. Both modes control the month
  // rather than seeding it once, because the year picker has to know which month is on
  // screen to keep it when only the year changes.
  const [leftMonth, setLeftMonth] = useState<Date>(() => getInitialMonth(props));

  // A re-seeded draft takes the visible month with it. Without this, an update that lands
  // in another month leaves the grid on the old month with no selection on it, so the grid
  // no longer matches the value Apply commits. The updater form keeps `leftMonth` out of
  // the dependencies: the month must follow a new value, not snap back while the user
  // browses.
  useEffect(() => {
    if (!open || draftDirty) {
      return;
    }
    const next = liveSingleRef.current;
    setDraftSingle(next);
    setLeftMonth((current) => getAnchoredMonth(current, next, false));
  }, [open, draftDirty, liveSingleKey]);

  useEffect(() => {
    if (!open || draftDirty) {
      return;
    }
    const next = liveRangeRef.current;
    setDraftRange(next);
    setLeftMonth((current) => getAnchoredMonth(current, next?.from, true));
  }, [open, draftDirty, liveRangeKey]);

  const handleDraftSingle = (date: Date | undefined) => {
    setDraftDirty(true);
    setDraftSingle(date);
  };

  const handleDraftRange = (range: DateRange | undefined) => {
    setDraftDirty(true);
    setDraftRange(range);
  };
  // `null` while the day grid is up. Otherwise the calendar whose caption opened the year
  // picker, so choosing a year returns to that same month rather than to the left one.
  const [yearPickerAnchor, setYearPickerAnchor] = useState<'left' | 'right' | null>(null);

  const label = getTriggerLabel(props);
  const hasSelection = props.mode === 'single' ? !!props.selected : !!props.selected?.from;
  // `react-day-picker`'s `weekStartsOn` only accepts the 0–6 literal union; values
  // outside that range are a caller bug, not something this wrapper can validate.
  const dayPickerWeekStartsOn = weekStartsOn as 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined;
  const calendarDisabled = maxDate ? { after: maxDate } : undefined;
  const weekdayFormatters = {
    formatWeekdayName: (day: Date) => formatWeekdayName(day, locale),
  };

  const rightMonth = addMonths(leftMonth, 1);
  const yearPickerMonth = yearPickerAnchor === 'right' ? rightMonth : leftMonth;

  const handleSelectYear = (year: number) => {
    // Normalize to the first before changing the year. A displayed month can retain the
    // selected day (for example February 29), and `setYear` would otherwise overflow it
    // into March when the target year is not a leap year.
    const picked = setYear(startOfMonth(yearPickerMonth), year);
    setLeftMonth(yearPickerAnchor === 'right' ? subMonths(picked, 1) : picked);
    setYearPickerAnchor(null);
  };

  // `react-day-picker` renders the caption as a plain `<span>`; swapping in a `Button`
  // through `components` is what makes the header clickable. One factory per side so a
  // range picker knows which of its two captions was used.
  const makeCaptionLabel = (anchor: 'left' | 'right') => {
    const CaptionLabel = ({ children }: HTMLAttributes<HTMLSpanElement>) => (
      <CalendarCaptionToggle expanded={false} onClick={() => setYearPickerAnchor(anchor)}>
        {children}
      </CalendarCaptionToggle>
    );
    CaptionLabel.displayName = 'DatePickerCaptionLabel';
    return CaptionLabel;
  };

  const handleOpenChange = (next: boolean) => {
    if (next) {
      // Re-seed the draft from the last *committed* selection every time the popover
      // opens — not just on mount — so a stale draft from a cancelled prior open (or a
      // prop change while closed) never resurfaces.
      if (props.mode === 'single') {
        setDraftSingle(props.selected);
      } else {
        setDraftRange(props.selected);
      }
      setDraftDirty(false);
      setLeftMonth(getInitialMonth(props));
      // Always reopen on the day grid, never on a year picker left up from last time.
      setYearPickerAnchor(null);
    }
    setOpen(next);
  };

  const handleApply = () => {
    if (props.mode === 'single') {
      props.onSelect(draftSingle);
    } else {
      props.onSelect(draftRange);
    }
    setOpen(false);
  };

  // Draft state simply isn't re-seeded until the next open (see `handleOpenChange`), so
  // dismissing here is enough to discard it — nothing to reset.
  const handleCancel = () => setOpen(false);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-empty={!hasSelection}
          className={cn(
            'w-full justify-start gap-2 font-normal data-[empty=true]:text-foreground-secondary',
            className,
          )}
          data-testid={props['data-testid']}
        >
          <CalendarIcon className="size-4 shrink-0" aria-hidden />
          <span className="truncate">{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        variant={variant}
        className={cn(
          'w-auto overflow-hidden rounded-lg bg-background-surface p-0',
          contentClassName,
        )}
        align="start"
      >
        {props.mode === 'single' ? (
          <div className="flex flex-col items-center gap-3 p-3">
            {yearPickerAnchor ? (
              <YearPicker
                displayMonth={yearPickerMonth}
                locale={locale}
                maxDate={maxDate}
                onSelectYear={handleSelectYear}
                onDismiss={() => setYearPickerAnchor(null)}
              />
            ) : (
              <Calendar
                mode="single"
                selected={draftSingle}
                onSelect={handleDraftSingle}
                month={leftMonth}
                onMonthChange={setLeftMonth}
                disabled={calendarDisabled}
                weekStartsOn={dayPickerWeekStartsOn}
                locale={locale}
                formatters={weekdayFormatters}
                classNames={calendarClassNames}
                fixedWeeks
                components={{ CaptionLabel: makeCaptionLabel('left') }}
                className="p-0"
                style={calendarStyle}
                autoFocus
              />
            )}
            <div className="flex w-full items-center justify-between">
              <Button type="button" variant="ghost" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="button" variant="default" onClick={handleApply}>
                Apply
              </Button>
            </div>
          </div>
        ) : (
          <div className={cn('flex flex-col', RANGE_POPOVER_WIDTH)}>
            {yearPickerAnchor ? (
              <div className="p-3">
                <YearPicker
                  displayMonth={yearPickerMonth}
                  locale={locale}
                  maxDate={maxDate}
                  onSelectYear={handleSelectYear}
                  onDismiss={() => setYearPickerAnchor(null)}
                />
              </div>
            ) : (
              <div className="flex">
                <Calendar
                  mode="range"
                  selected={draftRange}
                  onSelect={handleDraftRange}
                  month={leftMonth}
                  onMonthChange={setLeftMonth}
                  disabled={calendarDisabled}
                  weekStartsOn={dayPickerWeekStartsOn}
                  locale={locale}
                  formatters={weekdayFormatters}
                  classNames={calendarClassNames}
                  fixedWeeks
                  showOutsideDays={false}
                  components={{ CaptionLabel: makeCaptionLabel('left') }}
                  style={calendarStyle}
                  autoFocus
                />
                <Calendar
                  mode="range"
                  selected={draftRange}
                  onSelect={handleDraftRange}
                  month={rightMonth}
                  onMonthChange={(month) => setLeftMonth(subMonths(month, 1))}
                  disabled={calendarDisabled}
                  weekStartsOn={dayPickerWeekStartsOn}
                  locale={locale}
                  formatters={weekdayFormatters}
                  classNames={calendarClassNames}
                  fixedWeeks
                  showOutsideDays={false}
                  components={{ CaptionLabel: makeCaptionLabel('right') }}
                  style={calendarStyle}
                />
              </div>
            )}
            <div className="flex items-center justify-between p-3">
              <Button type="button" variant="ghost" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="button" variant="default" onClick={handleApply}>
                Apply
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
